/**
 * RTK Query API slice for template management endpoints.
 * These endpoints use the /api/v1/contracting/admin/templates base path,
 * which is separate from the packages base path.
 */
import { baseApi } from '@/store/api/baseApi';
import {
  ApiTags,
  FieldMappingTagIds,
  PackageTagIds,
  TemplateTagIds,
} from '@/store/api/tags';

// --- Types ---

export type TagMappingStatus = 'RESOLVED' | 'UNRESOLVED';

export interface TagMappingResponse {
  tagName: string;
  fieldKey: string | null;
  dataType: string | null;
  description: string | null;
  status: TagMappingStatus;
}

export interface TemplateVersionResponse {
  id: number;
  templateId: number;
  versionNumber: number;
  fileStorageLocation: string;
  allTagsResolved: boolean;
  tagMappings: TagMappingResponse[];
  created: string;
}

export interface TemplateResponse {
  id: number;
  name: string;
  packageId: number;
  uploadAndSignatureRequired: boolean;
  requiresRegulatoryData: boolean;
  versions: TemplateVersionResponse[];
  created: string;
  updated: string;
}

// --- Request types ---

export interface ReplaceTemplateRequest {
  templateId: number;
  file: File;
}

export interface DeleteTemplateRequest {
  templateId: number;
}

export interface UpdateTemplateRequest {
  templateId: number;
  name?: string;
  uploadAndSignatureRequired?: boolean;
  requiresRegulatoryData?: boolean;
}

export interface DownloadTemplateResult {
  url: string;
  filename: string;
}

export interface DownloadTemplateRequest {
  templateId: number;
  versionNumber: number;
}

export interface GetFieldMappingsRequest {
  templateId: number;
  versionNumber: number;
}

// --- API Slice ---

const TEMPLATES_BASE_URL = '/api/v1/contracting/admin/templates';

export const templatesApi = baseApi.injectEndpoints({
  endpoints: build => ({
    deleteTemplate: build.mutation<void, DeleteTemplateRequest>({
      query: ({ templateId }) => ({
        url: `${TEMPLATES_BASE_URL}/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        { type: ApiTags.TEMPLATES, id: TemplateTagIds.LIST },
      ],
    }),

    replaceTemplate: build.mutation<TemplateResponse, ReplaceTemplateRequest>({
      query: ({ templateId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${TEMPLATES_BASE_URL}/${templateId}/versions`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        { type: ApiTags.TEMPLATES, id: TemplateTagIds.LIST },
      ],
    }),

    updateTemplate: build.mutation<TemplateResponse, UpdateTemplateRequest>({
      query: ({ templateId, ...body }) => ({
        url: `${TEMPLATES_BASE_URL}/${templateId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        { type: ApiTags.TEMPLATES, id: TemplateTagIds.LIST },
      ],
    }),

    downloadTemplate: build.query<
      DownloadTemplateResult,
      DownloadTemplateRequest
    >({
      query: ({ templateId, versionNumber }) => ({
        url: `${TEMPLATES_BASE_URL}/${templateId}/versions/${versionNumber}/download`,
      }),
      keepUnusedDataFor: 0,
    }),

    getFieldMappings: build.query<
      TagMappingResponse[],
      GetFieldMappingsRequest
    >({
      query: ({ templateId, versionNumber }) => ({
        url: `${TEMPLATES_BASE_URL}/${templateId}/versions/${versionNumber}/tags`,
      }),
      providesTags: (result, error, { templateId }) => [
        {
          type: ApiTags.FIELD_MAPPINGS,
          id: `${FieldMappingTagIds.TEMPLATE}-${templateId}`,
        },
      ],
    }),
  }),
});

export const {
  useDeleteTemplateMutation,
  useReplaceTemplateMutation,
  useUpdateTemplateMutation,
  useLazyDownloadTemplateQuery,
  useGetFieldMappingsQuery,
} = templatesApi;
